import React from "react";
import { useTinyHost } from "@tiny/host-contract";
import { SegmentView } from "@tiny/api";
import {
  useDeleteSegmentMutation,
  usePutSegmentFindFilterQuery,
} from "@tiny/api";
import { useRefreshRegister } from "@tiny/components/src/RefreshProvider";
import { MenuAction } from "@tiny/components/src/DataList/DataList.types";
import useSearchable from "@tiny/components/src/hooks/useSearchable";
import useSelectable from "@tiny/components/src/hooks/useSelectable";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import {
  getSegmentTypesByTags,
  isPredefinedSegment,
  pageableDefault,
} from "./SegmentList.helpers";
import { SegmentListProps } from "./SegmentList.types";
import useCustomSegmentFeatures from "../../hooks/useCustomSegmentFeatures";
import { Tag } from "../../models/tags/Tag.types";

export function useSegmentList(props: SegmentListProps) {
  const {
    enableSelectionActions = true,
    selected: selectedProp,
    setSelected: setSelectedProp,
    selectionDisabled,
    visible = true,
    onCloneSuccess,
    onDeleteSuccess,
    pageName = "",
    segmentIds,
    aiEnabled,
    segmentTypes,
  } = props;

  const { showUpgradePopup, hasRightsToAccess } = useCustomSegmentFeatures();

  const {
    search,
    changePage,
    changeSearch,
    changeSize,
    changeSort,
    searchDebounced,
    pageable,
  } = useSearchable(pageableDefault, pageName);

  const {
    deselectAll,
    selected,
    toggleSelect,
    toggleSelectAll,
    removeSelected,
  } = useSelectable<SegmentView>({
    idKey: "id",
    selected: selectedProp,
    setSelected: setSelectedProp,
  });

  const { capabilities } = useTinyHost();
  const isAiTrafficControl = capabilities.aiTrafficControlEnabled;

  const { navigate } = useTinyHost();

  const { mdUp } = useBreakpoints();

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  const [itemToDelete, setItemToDelete] = React.useState<SegmentView>();

  const [filtersOpen, setFiltersOpen] = React.useState(false);

  const [sortOpen, setSortOpen] = React.useState(false);

  const [selectedTags, setSelectedTags] = React.useState<Tag[]>([]);

  const [cloneSegment, setCloneSegment] = React.useState<SegmentView>();

  const [isAudienceSummaryOpen, setIsAudienceSummaryOpen] =
    React.useState(false);

  const [selectedItem, setSelectedItem] = React.useState<SegmentView>();

  const [openExportAudienceDialog, setOpenExportAudienceDialog] =
    React.useState(false);

  const [openExportToExistingDialog, setOpenExportToExistingDialog] =
    React.useState(false);
  const [selectedItemForExport, setSelectedItemForExport] =
    React.useState<SegmentView>();

  const {
    data: { content: rawData = [], totalElements: totalCount } = {},
    isLoading,
    refetch: refetchSegments,
  } = usePutSegmentFindFilterQuery(
    {
      searchTextName: searchDebounced,
      segmentTypes: getSegmentTypesByTags(selectedTags, segmentTypes),
      aiEnabled: aiEnabled ? true : isAiTrafficControl ? false : undefined,
    },
    {
      page: pageable.page,
      size: segmentIds ? 1000 : pageable.size,
      sort: pageable.sort,
      countAllCustomers: false,
    },
    {
      keepPreviousData: true,
      enabled: !!capabilities.accountId,
    }
  );

  const data = segmentIds
    ? rawData.filter((item) => segmentIds.includes(item.id!))
    : rawData;

  useRefreshRegister(async () => {
    await refetchSegments();
  });

  const {
    mutateAsync: deleteSegmentMutateAsync,
    isLoading: isDeleteSegmentLoading,
  } = useDeleteSegmentMutation();

  const deleteList = React.useMemo(() => {
    return itemToDelete ? [itemToDelete] : selected;
  }, [itemToDelete, selected]);

  const openFilters = React.useCallback(() => {
    setFiltersOpen(true);
  }, []);

  const closeFilters = React.useCallback(() => {
    setFiltersOpen(false);
  }, []);

  const openSort = React.useCallback(() => {
    setSortOpen(true);
  }, []);

  const closeSort = React.useCallback(() => {
    setSortOpen(false);
  }, []);

  const openDeleteDialog = React.useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  const openDeleteRowDialog = React.useCallback((item: SegmentView) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  }, []);

  const closeDeleteDialog = React.useCallback(() => {
    setDeleteDialogOpen(false);
  }, []);

  const openAddExportAudienceDialog = (item: SegmentView) => {
    setOpenExportAudienceDialog(true);
    setSelectedItem(item);
  };

  const handleCloseExportAudienceDialog = () => {
    setOpenExportAudienceDialog(false);
  };

  const openAddExportToExistingDialog = (item: SegmentView) => {
    setSelectedItemForExport(item);
    setOpenExportToExistingDialog(true);
  };

  const handleCloseExportToExistingDialog = () => {
    setOpenExportToExistingDialog(false);
    setSelectedItemForExport(undefined);
  };
  const deleteSegments = React.useCallback(async () => {
    const ids = deleteList.map((item) => item.id!);
    await deleteSegmentMutateAsync({
      data: {
        ids,
      },
    });
    setDeleteDialogOpen(false);
    changePage(0);
    if (onDeleteSuccess) {
      onDeleteSuccess();
    } else {
      refetchSegments();
    }
    if (itemToDelete) {
      removeSelected(itemToDelete);
      setItemToDelete(undefined);
    } else {
      deselectAll();
    }
  }, [
    changePage,
    deleteList,
    deleteSegmentMutateAsync,
    deselectAll,
    itemToDelete,
    onDeleteSuccess,
    refetchSegments,
    removeSelected,
  ]);

  const editSegment = React.useCallback(
    (item: SegmentView) => {
      const newLocation = {
        pathname: `/main/audience/edit-segments/${item.id}`,
        state: {
          backUrl: { pathname: location.pathname, search: location.search },
        },
      };
      navigate.to(newLocation.pathname, { state: newLocation.state });
    },
    [navigate]
  );

  const openClone = React.useCallback(
    (item: SegmentView) => {
      if (hasRightsToAccess(true)) {
        setCloneSegment(item);
      } else {
        showUpgradePopup();
      }
    },
    [hasRightsToAccess, showUpgradePopup]
  );

  const closeClone = React.useCallback(() => {
    setCloneSegment(undefined);
  }, []);

  const successClone = React.useCallback(() => {
    if (onCloneSuccess) {
      onCloneSuccess();
    } else {
      refetchSegments();
    }
  }, [onCloneSuccess, refetchSegments]);

  const saveTags = React.useCallback((tags?: Tag[]) => {
    setSelectedTags(tags || []);
  }, []);

  const removeTag = React.useCallback((tag: Tag) => {
    setSelectedTags((prev) => prev.filter((item) => item.id !== tag.id));
  }, []);

  const moreActions = React.useMemo<MenuAction<SegmentView>[]>(
    () => [
      {
        name: "Edit",
        onClickAction: (item: SegmentView) => editSegment(item),
        visible: (item: SegmentView) => {
          return item.segmentType === "CUSTOM";
        },
      },
      {
        name: "Clone",
        onClickAction: (item: SegmentView) => openClone(item),
        // CloneSegmentDialog clones a segment definition; a source has none, and this
        // list can now be filtered to show sources.
        visible: (item: SegmentView) => {
          return item.segmentType === "CUSTOM";
        },
      },
      {
        name: "Delete",
        onClickAction: (item: SegmentView) => openDeleteRowDialog(item),
        visible: (item: SegmentView) => {
          return item.segmentType === "CUSTOM";
        },
      },
      {
        name: "Export as New Source",
        onClickAction: (item: SegmentView) => openAddExportAudienceDialog(item),
        visible: (item: SegmentView) => {
          return item.segmentType === "CUSTOM";
        },
      },
      {
        name: "Export to Existing Source",
        onClickAction: (item: SegmentView) =>
          openAddExportToExistingDialog(item),
        visible: (item: SegmentView) => {
          return item.segmentType === "CUSTOM";
        },
      },
    ],
    [openClone, editSegment, openDeleteRowDialog]
  );

  const selectedDisabled = React.useMemo<SegmentView[]>(() => {
    return data.filter(
      (item) => selectionDisabled || isPredefinedSegment(item.segmentType)
    );
  }, [data, selectionDisabled]);

  const cloneOpen = !!cloneSegment;

  const handleOpenAudienceSummary = () => {
    setIsAudienceSummaryOpen(true);
  };

  const handleCloseAudienceSummary = () => {
    setIsAudienceSummaryOpen(false);
  };

  const selectedSegmentIds = React.useMemo(() => {
    return selected?.map((audience) => audience?.id || "") || [];
  }, [selected]);

  return {
    changePage,
    changeSearch,
    changeSize,
    changeSort,
    cloneOpen,
    cloneSegment,
    closeClone,
    closeDeleteDialog,
    closeFilters,
    closeSort,
    data,
    deleteDialogOpen,
    deleteList,
    deleteSegments,
    deselectAll,
    enableSelectionActions,
    filtersOpen,
    isDeleteSegmentLoading,
    isLoading,
    mdUp,
    moreActions,
    openDeleteDialog,
    openFilters,
    openSort,
    pageable,
    refetchSegments,
    removeTag,
    saveTags,
    search,
    selected,
    selectedDisabled,
    selectedTags,
    selectionDisabled,
    sortOpen,
    successClone,
    toggleSelect,
    toggleSelectAll,
    totalCount,
    visible,
    handleOpenAudienceSummary,
    handleCloseAudienceSummary,
    selectedSegmentIds,
    isAudienceSummaryOpen,
    openExportAudienceDialog,
    handleCloseExportAudienceDialog,
    selectedItem,
    openExportToExistingDialog,
    handleCloseExportToExistingDialog,
    selectedItemForExport,
  };
}
